import { Button } from '../ui/button';
import { MapPin, ExternalLink, X } from 'lucide-react';

const buildRedfinUrl = (address) => {
  if (!address) return '';
  return `https://www.google.com/search?q=redfin+${encodeURIComponent(address)}`;
};

const buildZillowUrl = (address) => {
  if (!address) return '';
  const slug = address.replace(/[,#]/g, '').replace(/\s+/g, '-');
  return `https://www.zillow.com/homes/${slug}_rb/`;
};

const flipName = (name) => {
  if (!name || !name.includes(',')) return name;
  const [last, first] = name.split(',', 2);
  return `${first.trim()} ${last.trim()}`;
};

export function RedfinModal({ address, onClose }) {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-background border rounded-sm shadow-2xl w-[92vw] max-w-5xl h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="redfin-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <MapPin className="w-5 h-5 text-red-500 shrink-0" />
            <div className="min-w-0">
              <h2 className="font-heading font-semibold text-sm truncate">{address}</h2>
              <p className="text-xs text-muted-foreground">Property details via Redfin</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a href={buildRedfinUrl(address)} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-sm hover:bg-red-700 transition-colors"
              data-testid="redfin-open-new-tab"
            >
              Open in Redfin <ExternalLink className="w-3 h-3" />
            </a>
            <a href={buildZillowUrl(address)} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-colors"
              data-testid="zillow-open-new-tab"
            >
              Open in Zillow <ExternalLink className="w-3 h-3" />
            </a>
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-redfin-modal">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden">
          <iframe
            src={`https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`}
            title="Google Maps" className="w-full h-full border-0"
            allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </div>
  );
}

export function GoogleSearchModal({ search, onClose }) {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-background border rounded-sm shadow-2xl w-[92vw] max-w-5xl h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="google-search-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b shrink-0">
          <div className="min-w-0">
            <h2 className="font-heading font-semibold text-sm truncate">{search.name}</h2>
            {search.address && <p className="text-xs text-muted-foreground">{search.address}</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a
              href={`https://www.google.com/search?q=${encodeURIComponent(flipName(search.name) + (search.address ? ' ' + search.address : ''))}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 transition-colors"
            >
              Open in Google <ExternalLink className="w-3 h-3" />
            </a>
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-google-modal">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden">
          <iframe
            src={`https://www.google.com/search?igu=1&q=${encodeURIComponent(flipName(search.name) + (search.address ? ' ' + search.address : ''))}`}
            title="Google Search" className="w-full h-full border-0" referrerPolicy="no-referrer"
          />
        </div>
      </div>
    </div>
  );
}
